import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { DriverListing } from '@src/driver/domain/DriverList';
import { DriverForm } from '@src/driver/DriverForm';
import { useUpdateDriver } from '@src/driver/hooks/useUpdateDriver';
import { ConditionalFieldsContext } from '@src/orders/PermitFormSections/ConditionalFieldsContext';
import { DriverInformationInputsSchema } from '@src/orders/schemas/DriverInformationInputsSchema';
import {
  DriverEmailField,
  DriverLicenseNumberField,
  DriverNameField,
  DriverPhoneField,
} from '@src/permitField/DriverInformationFields';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

interface Props {
  clientId: string;
  onUpdate: (newDriver: DriverListing) => void;
  driver: DriverListing;
}

export const EditDriverForm = ({ clientId, driver, onUpdate }: Props) => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(DriverInformationInputsSchema),
    defaultValues: {
      [DriverNameField.fieldName]: driver.name,
      [DriverEmailField.fieldName]: driver.email,
      [DriverPhoneField.fieldName]: driver.phone,
      [DriverLicenseNumberField.fieldName]: driver.licenseNumber,
    },
  });

  const { updateDriver, isLoading } = useUpdateDriver({
    clientId,
    driverId: driver.id,
    onSuccess: (updatedDriver) => {
      onUpdate(updatedDriver);
    },
  });

  const submitForm: SubmitHandler<
    yup.InferType<typeof DriverInformationInputsSchema>
  > = (data) => {
    updateDriver(data);
  };

  return (
    <ConditionalFieldsContext.Provider value={{ disabledFields: [] }}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(submitForm)(e);
        }}
      >
        <Stack gap={theme.spacing['3']}>
          <DriverForm control={control} />
          <Divider
            sx={{
              marginTop: theme.spacing['6'],
              marginBottom: theme.spacing['6'],
            }}
          />
          <Stack alignItems="flex-end">
            <Button disabled={isLoading} type="submit" variant="contained">
              {t('save')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </ConditionalFieldsContext.Provider>
  );
};
